import { useEffect, useState } from 'react';
import { fhirClient } from 'src/App';
import { Task } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/Task';

const NON_ACTIONABLE_STATUSES = ['cancelled', 'entered-in-error', 'rejected', 'failed'];
const TASK_PAGE_SIZE = 100;

interface RevisitTaskValidationResult {
  isValid: boolean;
  message: string;
  latestTaskStatus?: string;
  latestTask?: Task;
  isLoading: boolean;
}

type RevisitTaskValidationState = Omit<RevisitTaskValidationResult, 'isLoading'> & {
  resolvedKey: string | null;
};

const authoredTime = (task?: Task): number =>
  new Date(task?.authoredOn || task?.meta?.lastUpdated || 0).getTime();

const byAuthoredDesc = (a: Task, b: Task): number => authoredTime(b) - authoredTime(a);

const getRevisitActivityDefinitionId = (checkListMetadata: string): string | null => {
  try {
    const metadata = JSON.parse(checkListMetadata);
    const activityDefinitionRef = metadata?.ui?.data?.activityDefinitionRef;
    const activityDefinitionId = activityDefinitionRef
      ? activityDefinitionRef.split('/')?.[1]
      : null;

    return typeof activityDefinitionId === 'string' && activityDefinitionId
      ? activityDefinitionId
      : null;
  } catch (err) {
    return null;
  }
};

export const useRevisitTaskValidation = (
  patientId: string | null,
  checkListData: any
): RevisitTaskValidationResult => {
  const checkListMetadata: string | null = checkListData?.metadata ?? null;

  // Identifies the inputs a stored result belongs to. `isLoading` is derived from it on every
  // render instead of being stored, so a caller can never act on a result that was resolved for
  // previous inputs - that stale read is what made the first open of the step always fail.
  const requestKey = `${patientId ?? ''}|${checkListMetadata ?? ''}`;

  const [state, setState] = useState<RevisitTaskValidationState>({
    isValid: false,
    message: 'Loading...',
    resolvedKey: null,
  });

  useEffect(() => {
    let cancelled = false;
    const key = `${patientId ?? ''}|${checkListMetadata ?? ''}`;

    const resolve = (result: Omit<RevisitTaskValidationState, 'resolvedKey'>) => {
      if (!cancelled) {
        setState({ ...result, resolvedKey: key });
      }
    };

    if (!patientId) {
      resolve({ isValid: false, message: 'No patient ID provided' });
      return undefined;
    }

    if (!checkListMetadata) {
      resolve({ isValid: false, message: 'No checkListData metadata provided' });
      return undefined;
    }

    const revisitActivityDefinitionId = getRevisitActivityDefinitionId(checkListMetadata);

    if (!revisitActivityDefinitionId) {
      resolve({
        isValid: false,
        message: 'No activityDefinitionId found in checklist metadata',
      });
      return undefined;
    }

    const validateRevisitTask = async () => {
      try {
        // `_sort=-authored` was silently ignored by the FHIR proxy (the correct search parameter
        // is `authored-on`), so results came back in lastUpdated order. Narrowing by
        // instantiates-canonical is not an option either - the proxy ignores that parameter too
        // and returns every assessment task - so the page has to be wide enough to hold them all
        // and the filtering below is what actually selects the revisit tasks.
        const response = await fhirClient.search<Task>(
          `Task?subject=${patientId}&code=assessment` +
            `&_sort=-authored-on&_count=${TASK_PAGE_SIZE}`
        );

        const revisitTasks = (response?.entry ?? [])
          .map((entry) => entry?.resource)
          .filter((resource): resource is Task => resource?.resourceType === 'Task')
          .filter((task) => {
            const instantiatesCanonical = task?.instantiatesCanonical;
            if (!instantiatesCanonical) return false;

            const activityDefRef = Array.isArray(instantiatesCanonical)
              ? instantiatesCanonical[0]
              : instantiatesCanonical;

            return activityDefRef?.split('/')?.[1] === revisitActivityDefinitionId;
          })
          .filter((task) => !NON_ACTIONABLE_STATUSES.includes(task.status));

        if (!revisitTasks.length) {
          resolve({ isValid: false, message: 'No revisit tasks found - step executable' });
          return;
        }

        // A patient can hold several revisit tasks at once, so pick the one that still has to be
        // filled in rather than whichever was authored last - only fall back to the newest
        // completed task so the caller can report the step as already done.
        const sortedRevisitTasks = [...revisitTasks].sort(byAuthoredDesc);
        const openTask = sortedRevisitTasks.find((task) => task.status !== 'completed');
        const latestTask = openTask ?? sortedRevisitTasks[0];
        const taskStatus = latestTask?.status;

        resolve({
          isValid: !openTask,
          message: openTask
            ? `Revisit task ${taskStatus} - step executable`
            : 'All questions answered - step not executable',
          latestTaskStatus: taskStatus,
          latestTask,
        });
      } catch (error) {
        console.error('Error validating revisit task:', error);
        resolve({
          isValid: false,
          message: 'Unable to check revisit task status - allowing execution',
        });
      }
    };

    validateRevisitTask();

    return () => {
      cancelled = true;
    };
  }, [patientId, checkListMetadata]);

  return { ...state, isLoading: state.resolvedKey !== requestKey };
};

export default useRevisitTaskValidation;
